import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Circle,
  CheckCircle2,
  MinusCircle,
  Play,
  RefreshCw,
} from 'lucide-react';
import type {
  ChecklistItemStatus,
  ChecklistPhase,
  ChecklistGroup,
  ChecklistItem,
} from '@monitoring-panel/types';
import { useChecklist, useUpdateChecklistItem } from '@/hooks/useExecutionNotes';
import { Skeleton } from './Skeleton';
import { cn, formatRelativeTime } from '@/lib/utils';

interface ExecutionChecklistProps {
  campaignName: string;
}

/** Cycle: pending -> wip -> done -> na -> pending */
function nextStatus(current: ChecklistItemStatus): ChecklistItemStatus {
  switch (current) {
    case 'pending':
      return 'wip';
    case 'wip':
      return 'done';
    case 'done':
      return 'na';
    case 'na':
      return 'pending';
    default:
      return 'pending';
  }
}

/** Render the status icon for a given status */
function StatusIcon({
  status,
  size = 16,
}: {
  status: ChecklistItemStatus;
  size?: number;
}) {
  switch (status) {
    case 'done':
      return <CheckCircle2 size={size} className="text-adv-success flex-shrink-0" />;
    case 'wip':
      return <Play size={size} className="text-adv-info flex-shrink-0" />;
    case 'na':
      return <MinusCircle size={size} className="text-adv-tertiary flex-shrink-0" />;
    case 'pending':
    default:
      return <Circle size={size} className="text-adv-tertiary flex-shrink-0" />;
  }
}

/** Derive a group's display status from its children */
function deriveGroupStatus(children: ChecklistItem[]): ChecklistItemStatus {
  if (children.length === 0) return 'pending';

  const allDoneOrNa = children.every(
    (child) => child.status === 'done' || child.status === 'na',
  );
  if (allDoneOrNa) return 'done';

  const hasWip = children.some((child) => child.status === 'wip');
  const hasDone = children.some((child) => child.status === 'done');
  if (hasWip || hasDone) return 'wip';

  return 'pending';
}

/** Derive a phase's display status from all its groups */
function derivePhaseStatus(phase: ChecklistPhase): ChecklistItemStatus {
  if (phase.groups.length === 0) return 'pending';

  const groupStatuses = phase.groups.map((group) =>
    group.children.length > 0 ? deriveGroupStatus(group.children) : group.status,
  );

  const allDoneOrNa = groupStatuses.every((s) => s === 'done' || s === 'na');
  if (allDoneOrNa) return 'done';

  const hasWip = groupStatuses.some((s) => s === 'wip');
  const hasDone = groupStatuses.some((s) => s === 'done');
  if (hasWip || hasDone) return 'wip';

  return 'pending';
}

/** Count all leaf items across phases for the summary line */
export function countChecklistItems(phases: ChecklistPhase[]): {
  total: number;
  done: number;
  na: number;
  wip: number;
} {
  let total = 0;
  let done = 0;
  let na = 0;
  let wip = 0;

  for (const phase of phases) {
    for (const group of phase.groups) {
      if (group.children.length > 0) {
        for (const child of group.children) {
          total++;
          if (child.status === 'done') done++;
          if (child.status === 'na') na++;
          if (child.status === 'wip') wip++;
        }
      } else {
        total++;
        if (group.status === 'done') done++;
        if (group.status === 'na') na++;
        if (group.status === 'wip') wip++;
      }
    }
  }

  return { total, done, na, wip };
}

/** Individual checklist item row (either a group without children, or a child item) */
function ChecklistItemRow({
  item,
  indented,
  onToggleStatus,
}: {
  item: ChecklistGroup | ChecklistItem;
  indented?: boolean;
  onToggleStatus: (itemId: string, newStatus: ChecklistItemStatus) => void;
}) {
  const isNa = item.status === 'na';
  const isWip = item.status === 'wip';

  return (
    <div
      className={cn(
        'flex items-center gap-2 py-1.5',
        indented && 'pl-6',
      )}
    >
      <button
        onClick={() => onToggleStatus(item.id, nextStatus(item.status))}
        className="flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:p-0.5 rounded-adv-md hover:bg-adv-sunken transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus"
        title={`Status: ${item.status} — Click to change`}
      >
        <StatusIcon status={item.status} />
      </button>
      <span
        className={cn(
          'text-adv-body-m select-none',
          isNa && 'line-through text-adv-tertiary',
          isWip && 'text-adv-info font-bold',
          !isNa && !isWip && 'text-adv-primary',
        )}
      >
        {item.label}
      </span>
      {isWip && item.updatedAt && (
        <span className="ml-auto text-adv-body-s text-adv-info flex-shrink-0">
          {formatRelativeTime(item.updatedAt)}
        </span>
      )}
    </div>
  );
}

/** A group with children: shows the group header (expandable) and children underneath */
function ChecklistGroupSection({
  group,
  onToggleStatus,
}: {
  group: ChecklistGroup;
  onToggleStatus: (itemId: string, newStatus: ChecklistItemStatus) => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const hasChildren = group.children.length > 0;

  if (!hasChildren) {
    return <ChecklistItemRow item={group} onToggleStatus={onToggleStatus} />;
  }

  const derivedStatus = deriveGroupStatus(group.children);

  return (
    <div>
      <div className="flex items-center gap-1 py-1.5">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-[20px] sm:min-h-0 sm:p-0.5 rounded-adv-md hover:bg-adv-sunken transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus"
          title={isOpen ? 'Collapse' : 'Expand'}
        >
          {isOpen ? (
            <ChevronDown size={14} className="text-adv-secondary" />
          ) : (
            <ChevronRight size={14} className="text-adv-secondary" />
          )}
        </button>
        <span className="flex items-center justify-center sm:p-0.5">
          <StatusIcon status={derivedStatus} />
        </span>
        <span
          className={cn(
            'text-adv-body-m font-bold select-none',
            derivedStatus === 'na' ? 'line-through text-adv-tertiary' : 'text-adv-primary',
          )}
        >
          {group.label}
        </span>
      </div>

      {isOpen && (
        <div>
          {group.children.map((child) => (
            <ChecklistItemRow
              key={child.id}
              item={child}
              indented
              onToggleStatus={onToggleStatus}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** A single phase section with its header and groups */
function PhaseSection({
  phase,
  defaultOpen,
  onToggleStatus,
}: {
  phase: ChecklistPhase;
  defaultOpen: boolean;
  onToggleStatus: (itemId: string, newStatus: ChecklistItemStatus) => void;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const phaseStatus = derivePhaseStatus(phase);

  return (
    <div className="border-b border-adv-border-subtle last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full py-2.5 px-1 min-h-[44px] sm:min-h-0 hover:bg-adv-sunken transition-colors rounded-adv-md focus-visible:outline-none focus-visible:shadow-adv-focus"
      >
        {isOpen ? (
          <ChevronDown size={16} className="text-adv-secondary flex-shrink-0" />
        ) : (
          <ChevronRight size={16} className="text-adv-secondary flex-shrink-0" />
        )}
        <StatusIcon status={phaseStatus} />
        <span className="text-adv-body-m font-bold text-adv-primary">{phase.label}</span>
      </button>

      {isOpen && (
        <div className="pl-2 pb-2">
          {phase.groups.map((group) => (
            <ChecklistGroupSection
              key={group.id}
              group={group}
              onToggleStatus={onToggleStatus}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Loading skeleton for the checklist */
function ChecklistSkeleton() {
  return (
    <div className="space-y-3 p-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-5 w-32" />
          <div className="pl-4 space-y-1.5">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-44" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ExecutionChecklist({ campaignName }: ExecutionChecklistProps) {
  const { data, isLoading, isError, error, refetch } = useChecklist(campaignName);
  const updateItem = useUpdateChecklistItem(campaignName);

  const handleToggleStatus = (itemId: string, newStatus: ChecklistItemStatus) => {
    updateItem.mutate({ itemId, status: newStatus });
  };

  if (isLoading) {
    return <ChecklistSkeleton />;
  }

  if (isError) {
    return (
      <div className="adv-alert flex items-center gap-2 p-3">
        <span className="flex-1">
          {error instanceof Error ? error.message : 'Failed to load checklist'}
        </span>
        <button
          onClick={() => refetch()}
          className="adv-btn adv-btn--danger adv-btn--sm min-h-[44px] sm:min-h-0"
        >
          <RefreshCw size={12} />
          Retry
        </button>
      </div>
    );
  }

  if (!data?.checklist?.phases) {
    return null;
  }

  // Auto-open the first phase that isn't fully done; if all done, open the last one
  const phases = data.checklist.phases;
  const firstIncompleteIndex = phases.findIndex(
    (phase) => derivePhaseStatus(phase) !== 'done',
  );
  const autoOpenIndex = firstIncompleteIndex >= 0 ? firstIncompleteIndex : phases.length - 1;

  return (
    <div>
      {phases.map((phase, index) => (
        <PhaseSection
          key={phase.id}
          phase={phase}
          defaultOpen={index === autoOpenIndex}
          onToggleStatus={handleToggleStatus}
        />
      ))}
    </div>
  );
}
